/**
 * Compute axis-aligned bounding box of a rectangle with rotation (degrees).
 * Center of rotation is the rectangle center.
 * @param {{ x: number, y: number, width: number, height: number, rotation?: number }} rect
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number }}
 */
function getRectAABB(rect) {
    const {x, y, width, height} = rect
    const rotation = (rect.rotation ?? 0) * (Math.PI / 180)
    const cx = x + width / 2
    const cy = y + height / 2

    const halfW = width / 2
    const halfH = height / 2
    const corners = [
        [-halfW, -halfH],
        [halfW, -halfH],
        [halfW, halfH],
        [-halfW, halfH],
    ]

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    const cos = Math.cos(rotation)
    const sin = Math.sin(rotation)
    for (const [dx, dy] of corners) {
        const rx = dx * cos - dy * sin + cx
        const ry = dx * sin + dy * cos + cy
        minX = Math.min(minX, rx)
        minY = Math.min(minY, ry)
        maxX = Math.max(maxX, rx)
        maxY = Math.max(maxY, ry)
    }

    return {minX, minY, maxX, maxY}
}

/**
 * Get content bounds from editor state (all nodes' geometry merged into one AABB).
 * Uses state.nodes and state.order; respects visible nodes only (no hidden/locked filter here unless you add it).
 *
 * @param {{ nodes: Map<string, object>, order?: string[] }} state - Editor state from getState()
 * @param {{ padding?: number }} options - Optional padding (px) added to width/height and subtracted from x/y
 * @returns {{ x: number, y: number, width: number, height: number } | null} Bounds or null if no nodes
 */
export function getContentBounds(state, options = {}) {
    const padding = options.padding ?? 0
    const nodes = state?.nodes
    const order = state?.order
    if (!nodes?.size) return null

    const ids = Array.isArray(order) && order.length > 0 ? order : Array.from(nodes.keys())
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    let hasAny = false

    for (const id of ids) {
        const model = nodes.get(id)
        if (!model?.geometry) continue

        const g = model.geometry
        const x = g.x ?? 0
        const y = g.y ?? 0
        const w = g.width ?? (model.type === 'text' ? 100 : 1)
        const h = g.height ?? (model.type === 'text' ? 50 : 1)

        const aabb = getRectAABB({
            x,
            y,
            width: w,
            height: h,
            rotation: g.rotation ?? 0,
        })

        minX = Math.min(minX, aabb.minX)
        minY = Math.min(minY, aabb.minY)
        maxX = Math.max(maxX, aabb.maxX)
        maxY = Math.max(maxY, aabb.maxY)
        hasAny = true
    }

    if (!hasAny) return null

    return {
        x: Math.floor(minX) - padding,
        y: Math.floor(minY) - padding,
        width: Math.ceil(maxX - minX) + 2 * padding,
        height: Math.ceil(maxY - minY) + 2 * padding,
    }
}
